import { OccupationTrackGroupDrawDirection } from "@opstel/query/models/trackOccupation";
import { DateTimeObj } from "@shared/common/types/dateTimeObj";
import { roundDown, roundUp } from "@shared/common/utils/luxonHelpers";
import { upperCaseFirst } from "@shared/common/utils/upperCaseFirst";
import { NsSpacingDefault } from "@shared/EDS/ns-eds-types";
import { DateTime } from "luxon";
import {
  TIMELINE_INTERVAL_IN_MINUTES,
  TOP_BAR_INPUT_HEIGHT,
  TRACK_HEIGHT,
  TRACK_ORDER_GAP,
} from "./layoutConstants";
import { TimeLineWeekdayName } from "./types";

export function getTrackHeight(maxOrder: number) {
  return TRACK_HEIGHT * maxOrder + TRACK_ORDER_GAP * (maxOrder - 1);
}

export function getTrackOccupationPosition(order: number) {
  return (order - 1) * (TRACK_HEIGHT + TRACK_ORDER_GAP);
}

export function getTrackGroupDrawPosition(
  order: number,
  maxOrder: number,
  drawDirection: OccupationTrackGroupDrawDirection,
): { top: number; bottom: number } {
  const topMost = () => -getHalfTrackGapHeight();
  const bottomMost = () => getBottomOfTrackFromOrder(1, maxOrder);
  const trackPosition = () => getTrackOccupationPosition(order) + 0.5 * TRACK_HEIGHT;

  switch (drawDirection) {
    case "Up":
      return {
        top: topMost(),
        bottom: trackPosition(),
      };
    case "Down":
      return {
        top: trackPosition(),
        bottom: bottomMost(),
      };
    case "Both":
      return { top: topMost(), bottom: bottomMost() };
    default:
      return { top: 0, bottom: 0 };
  }
}

function getBottomOfTrackFromOrder(order: number, maxOrder: number) {
  return (
    (maxOrder - order) * (TRACK_HEIGHT + TRACK_ORDER_GAP) + TRACK_HEIGHT + getHalfTrackGapHeight()
  );
}

function getHalfTrackGapHeight() {
  return getTrackGap().value / 2;
}

export function getTrackGap() {
  return NsSpacingDefault.spacing10;
}

export function getTopBarPadding() {
  return NsSpacingDefault.spacing4;
}

export function getTopBarHeight() {
  return getTopBarPadding().value * 2 + TOP_BAR_INPUT_HEIGHT;
}

/**
 * This function returns an array of TimeLineWeekdayName objects. Each object contains the name of the weekday and the number of hours in that day.
 * @param startDate
 * @param startTime
 * @param duration
 * @returns
 */
export function getTimeLineWeekdayNames(
  start: DateTimeObj,
  duration: { hours: number },
): TimeLineWeekdayName[] {
  const result: TimeLineWeekdayName[] = [];
  const startDateTime = DateTime.fromObject(start);
  for (let i = 0; i < duration.hours; i++) {
    const currentDateTime = startDateTime.plus({ hours: i });

    // Check if it is the first iteration or if the day has changed
    if (i === 0 || currentDateTime.hour === 0) {
      result.push({
        weekdayName: upperCaseFirst(currentDateTime.setLocale("nl").toFormat("cccc")),
        numberOfHoursInDay: 1,
      });
    } else {
      result[result.length - 1].numberOfHoursInDay++;
    }
  }

  return result;
}

export function getTimeLineDateTimes(scenarioStart: DateTime, scenarioEnd: DateTime): DateTime[] {
  const start = roundUp(scenarioStart, "minute", TIMELINE_INTERVAL_IN_MINUTES);
  const end = roundDown(scenarioEnd, "minute", TIMELINE_INTERVAL_IN_MINUTES);

  const result: DateTime<true>[] = [start];
  const scenarioLenghtInMinutes = end.diff(start).as("minutes");

  for (
    let minuteNumber = TIMELINE_INTERVAL_IN_MINUTES;
    minuteNumber <= scenarioLenghtInMinutes;
    minuteNumber += TIMELINE_INTERVAL_IN_MINUTES
  ) {
    const tijdLijnCurrentTime = start.plus({ minutes: minuteNumber });
    result.push(tijdLijnCurrentTime);
  }

  return result;
}
